import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Actions,
  getCommercialOdFinancialStats,
} from "../../../actions/admin.action";
import Alert from "../../../components/alert";
import { CommercialODFinancialStats } from "../../../dto";
import { ReactComponent as Loader } from "../../../images/loader.svg";
import { ForbiddenError } from "../../../middleware";
import { ReduxState } from "../../../reducers";
import { isLoadingActive } from "../../../utils";
import InfoCardList from "../infoCardList";
import { ReportSectionAuthStatus } from "../types";
import {
  fetchDataForSelectedMonth,
  getCommercialOdLoanSection,
} from "../utils";

interface CommercialOdLoanStatsProps {
  selectedDate: Date | null;
  onAuthorizationChange: ReportSectionAuthStatus;
}

export default function CommercialOdLoanStats({
  selectedDate,
  onAuthorizationChange,
}: CommercialOdLoanStatsProps) {
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.admin.loadingQueue
  );
  const commercialOdFinancialStats: CommercialODFinancialStats | null =
    useSelector((state: ReduxState) => state.admin.commercialOdFinancialStats);

  const [errorMessage, setErrorMessage] = useState("");
  const [notAuthorized, setNotAuthorized] = useState(false);

  const isLoading = isLoadingActive(loadingQueue, [
    Actions.REQUEST_GET_COMMERCIAL_OD_FINANCIAL_STATS,
  ]);

  useEffect(() => {
    if (!selectedDate) return;
    fetchDataForSelectedMonth(
      getCommercialOdFinancialStats,
      storeDispatch,
      selectedDate
    )
      .then(() => onAuthorizationChange("commercialOd", "authorized"))
      .catch((error) => {
        if (error instanceof ForbiddenError) {
          setNotAuthorized(true);
          onAuthorizationChange("commercialOd", "forbidden");
        } else {
          onAuthorizationChange("commercialOd", "error");
        }
      });
  }, [selectedDate, storeDispatch, onAuthorizationChange]);

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center dashboard-container p-3">
        <Loader />
      </div>
    );
  }

  if (notAuthorized)
    return (
      <div className="dashboard-container">
        <Alert
          type="danger"
          message="Not authorized to view B2B loan stats."
        />
      </div>
    );

  if (!commercialOdFinancialStats)
    return (
      <div className="d-flex align-items-baseline justify-content-center">
        <p className="text-muted">
          Unable to load summary stats. Please reload the page again.
        </p>
      </div>
    );

  const commercialOdLoanSection = getCommercialOdLoanSection(
    commercialOdFinancialStats
  );
  const { infoCards, sectionName } = commercialOdLoanSection;

  return (
    <div className="dashboard-container">
      <Alert
        message={errorMessage}
        type="info"
        canDismiss
        onDismissClick={() => setErrorMessage("")}
      />

      <section>
        <h3 className="user-page-title">{sectionName}</h3>
        <InfoCardList infoCards={infoCards} />
      </section>
    </div>
  );
}
